import type {
    AufgeloesterKatalog,
    AufgeloesterKnoten,
    Gesamtkatalog,
    KatalogAuszug,
    KatalogAuswahl,
    KatalogEintrag,
    MerkSchluessel,
} from './typen';
import { vorlageAusAuszug } from './vorlage';

/**
 * Auswahl → KatalogAuswahl: das, was Torte, Kachel, Suche, Zuletzt, Favorit
 * oder die Freitext-Kachel an den Schritt „Details“ übergeben.
 */

const KNOTEN_PRAEFIX = 'knoten:';
const EINTRAG_PRAEFIX = 'eintrag:';

/**
 * Pfad für Chip und Tooltip: bei einem Mangel die Namen bis zum Unterpunkt
 * („Absturz › Seitenschutz“), sonst der ganze Pfad.
 */
export function anzeigePfad(k: AufgeloesterKnoten): string[] {
    return k.typ === 'mangel' && k.pfad.length > 1 ? k.pfad.slice(0, -1) : [...k.pfad];
}

export function auswahlAusKnoten(k: AufgeloesterKnoten): KatalogAuswahl {
    return {
        art: 'knoten',
        katalogKey: k.id,
        katalogKategorie: k.kategorieId,
        vorlage: k.vorlage ?? null,
        pfad: anzeigePfad(k),
        pikto: k.pikto,
    };
}

/** Direkter Treffer aus dem Gesamtkatalog (Suche, Zuletzt, Favorit). */
export function auswahlAusEintrag(e: KatalogEintrag | KatalogAuszug, kategorieId: string | null, katalog: AufgeloesterKatalog): KatalogAuswahl {
    const kategorie = kategorieId ? katalog.knoten.get(kategorieId) : undefined;
    return {
        art: 'eintrag',
        katalogKey: null,
        katalogKategorie: kategorieId,
        vorlage: vorlageAusAuszug(e),
        pfad: kategorie ? [kategorie.name] : kategorieId ? [kategorieId] : [],
        pikto: kategorie?.pikto ?? 'allgemein',
    };
}

/** „Freitext in <Thema>“: übernimmt nur die Kategorie. */
export function auswahlFreitext(kategorie: AufgeloesterKnoten): KatalogAuswahl {
    return {
        art: 'freitext',
        katalogKey: null,
        katalogKategorie: kategorie.kategorieId,
        vorlage: null,
        pfad: [...kategorie.pfad],
        pikto: kategorie.pikto,
    };
}

/** knoten:<id> | eintrag:<eintragId> | null (Freitext). */
export function schluesselVon(a: KatalogAuswahl): MerkSchluessel | null {
    if (a.art === 'knoten' && a.katalogKey) return KNOTEN_PRAEFIX + a.katalogKey;
    if (a.art === 'eintrag' && a.vorlage?.eintragId) return EINTRAG_PRAEFIX + a.vorlage.eintragId;
    return null;
}

// ─── Nachschlagen (gecacht je Katalog bzw. je Gesamtkatalog) ─────────────────

interface KnotenKarten {
    /** Eintrags-ID → Standardmangel, der sie als quelle[0] trägt. */
    nachEintragId: ReadonlyMap<string, AufgeloesterKnoten>;
    /** Überschrift + Beschreibung → eigener Mangel (übernommen, ohne Verweis). */
    nachText: ReadonlyMap<string, AufgeloesterKnoten>;
}

const knotenKarten = new WeakMap<AufgeloesterKatalog, KnotenKarten>();

/**
 * Vergleichsschlüssel aus Überschrift und Beschreibung. Ein aus dem
 * Gesamtkatalog übernommener eigener Mangel trägt keinen Verweis auf den
 * Eintrag (Entscheid 28.09.) — er wird über seine Texte wiedererkannt.
 */
export function textSchluessel(titel: string | undefined, text: string | undefined): string {
    return `${(titel ?? '').trim()}\n${(text ?? '').trim()}`;
}

function kartenVonKatalog(katalog: AufgeloesterKatalog): KnotenKarten {
    const vorhanden = knotenKarten.get(katalog);
    if (vorhanden) return vorhanden;
    const nachEintragId = new Map<string, AufgeloesterKnoten>();
    const nachText = new Map<string, AufgeloesterKnoten>();
    for (const k of katalog.knoten.values()) {
        if (k.typ !== 'mangel' || !k.vorlage) continue;
        const eintragId = k.vorlage.eintragId;
        if (eintragId && !nachEintragId.has(eintragId)) nachEintragId.set(eintragId, k);
        if (k.herkunft === 'eigen' && !eintragId) {
            const s = textSchluessel(k.vorlage.titel, k.vorlage.text);
            if (s !== '\n' && !nachText.has(s)) nachText.set(s, k);
        }
    }
    const karten = { nachEintragId, nachText };
    knotenKarten.set(katalog, karten);
    return karten;
}

/**
 * Eintrags-ID → sichtbarer Mangel-Knoten, der sie als Standard (quelle[0])
 * trägt. Ein solcher Eintrag erscheint in Suche und Merkliste nur als Knoten.
 */
export function knotenNachEintragId(katalog: AufgeloesterKatalog): ReadonlyMap<string, AufgeloesterKnoten> {
    return kartenVonKatalog(katalog).nachEintragId;
}

/**
 * Sichtbarer Mangel-Knoten zu einem Eintrag des Gesamtkatalogs: der
 * Standardmangel, der ihn als Quelle führt, sonst ein eigener Mangel mit
 * gleicher Überschrift und Beschreibung (aus dem Gesamtkatalog übernommen).
 */
export function knotenZuEintrag(
    katalog: AufgeloesterKatalog,
    e: Pick<KatalogEintrag, 'id' | 'titel' | 'text'>,
): AufgeloesterKnoten | undefined {
    const karten = kartenVonKatalog(katalog);
    return karten.nachEintragId.get(e.id) ?? karten.nachText.get(textSchluessel(e.titel, e.text));
}

interface QuellenKarten {
    eintraege: ReadonlyMap<string, KatalogEintrag>;
    elternAbschnitt: ReadonlyMap<string, string>;
}

const quellenKarten = new WeakMap<Gesamtkatalog, QuellenKarten>();

function kartenVon(quelle: Gesamtkatalog): QuellenKarten {
    const vorhanden = quellenKarten.get(quelle);
    if (vorhanden) return vorhanden;
    const eintraege = new Map<string, KatalogEintrag>();
    for (const e of quelle.eintraege ?? []) if (!eintraege.has(e.id)) eintraege.set(e.id, e);
    const elternAbschnitt = new Map<string, string>();
    for (const a of quelle.abschnitte ?? []) if (a.eltern) elternAbschnitt.set(a.id, a.eltern);
    const karten = { eintraege, elternAbschnitt };
    quellenKarten.set(quelle, karten);
    return karten;
}

/** Eintrag des Gesamtkatalogs zu einer internen ID. */
export function eintragNachId(quelle: Gesamtkatalog | null, eintragId: string): KatalogEintrag | null {
    if (!quelle || !eintragId) return null;
    return kartenVon(quelle).eintraege.get(eintragId) ?? null;
}

/**
 * Kategorie eines Abschnitts laut Zuordnung; fehlt der Abschnitt dort,
 * zählt der nächste übergeordnete Abschnittskopf des Gesamtkatalogs.
 */
export function kategorieFuerAbschnitt(abschnitt: string, quelle: Gesamtkatalog | null, zuordnung: Record<string, string>): string | null {
    const eltern = quelle ? kartenVon(quelle).elternAbschnitt : null;
    const besucht = new Set<string>();
    let aktuell: string | undefined = typeof abschnitt === 'string' ? abschnitt.trim() : '';
    while (aktuell && !besucht.has(aktuell)) {
        besucht.add(aktuell);
        const kategorie = zuordnung && Object.prototype.hasOwnProperty.call(zuordnung, aktuell) ? zuordnung[aktuell] : undefined;
        if (typeof kategorie === 'string' && kategorie) return kategorie;
        aktuell = eltern?.get(aktuell);
    }
    return null;
}

/**
 * Merkliste-Schlüssel → Auswahl. 'eintrag:<id>' eines Eintrags, der
 * inzwischen als Knoten in der Torte steht, liefert den Knoten. Unbekannt,
 * ausgeblendet oder (bei eintrag:) Gesamtkatalog noch nicht geladen → null.
 */
export function auswahlAusSchluessel(
    s: MerkSchluessel,
    katalog: AufgeloesterKatalog,
    quelle: Gesamtkatalog | null,
    zuordnung: Record<string, string>,
): KatalogAuswahl | null {
    if (typeof s !== 'string') return null;
    if (s.startsWith(KNOTEN_PRAEFIX)) {
        const k = katalog.knoten.get(s.slice(KNOTEN_PRAEFIX.length));
        return k && k.typ === 'mangel' && k.vorlage ? auswahlAusKnoten(k) : null;
    }
    if (s.startsWith(EINTRAG_PRAEFIX)) {
        const eintragId = s.slice(EINTRAG_PRAEFIX.length);
        if (!eintragId) return null;
        const knoten = knotenNachEintragId(katalog).get(eintragId);
        if (knoten) return auswahlAusKnoten(knoten);
        const e = eintragNachId(quelle, eintragId);
        if (!e) return null;
        const uebernommen = knotenZuEintrag(katalog, e);
        if (uebernommen) return auswahlAusKnoten(uebernommen);
        return auswahlAusEintrag(e, kategorieFuerAbschnitt(e.abschnitt, quelle, zuordnung), katalog);
    }
    return null;
}
